import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { createSearchParams } from "react-router-dom";

/**
 * Encapsulates the reporting table filter feature: fetches the filter-options
 * catalog, derives the column options consumed by the <Filters> drawer, keeps
 * the applied/pending filter state, and exposes the handlers + request params
 * needed by the reporting table.
 *
 * Product-agnostic and shared by every reporting table across the app
 * (search ads, PMax, display ads, meta travel ads, AI connect, metasearch,
 * ...) so the filter drawer behaviour stays identical everywhere.
 *
 * Two ways to point the hook at a catalog:
 *  - Single fixed endpoint: pass `apiEndpoint` + `filterOptionsPath`, they're
 *    concatenated into the catalog URL.
 *  - Dynamic/resolved endpoint (e.g. Metasearch, where the catalog depends on
 *    the selected platform): pass the already-resolved `filterOptionsUrl`
 *    directly. Pass `null`/`undefined` to disable the feature entirely (e.g.
 *    platform doesn't support server-side filtering) — filterMetadata stays
 *    null and filterColumnOptions falls back to the visible METRICS columns.
 *
 * @param {object} params
 * @param {string} [params.apiEndpoint] - Base API URL, combined with
 *   `filterOptionsPath` to build the catalog URL.
 * @param {string} [params.filterOptionsPath] - Path appended to `apiEndpoint`.
 * @param {string|null} [params.filterOptionsUrl] - Full catalog URL, takes
 *   precedence over `apiEndpoint` + `filterOptionsPath` when provided.
 * @param {Array} [params.metaChannelIds] - Extra `meta_channel[]` filter used
 *   by Metasearch's Global Reporting catalog (zero/multiple platforms).
 *
 * @returns {{
 *   filterPayload: Array,
 *   filterMetadata: object|null,
 *   filterColumnOptions: Array,
 *   appliedFilters: Array,
 *   appliedOperator: string,
 *   filterOpenTrigger: number,
 *   tableFilterParams: object,
 *   isFilterMetadataLoading: boolean,
 *   isFilteringSupported: boolean,
 *   applyFilters: (payload: Array) => void,
 *   clearFilters: () => void,
 *   removeFilter: (index: number) => void,
 *   openFilters: () => void,
 *   markFiltersApplied: () => void,
 * }}
 */
export const useReportingFilters = ({
  apiEndpoint,
  filterOptionsPath,
  filterOptionsUrl,
  token,
  accountId,
  level,
  hotelId = [],
  groupId = [],
  campaign = [],
  metaChannelIds = [],
  currentDateRange = {},
  segmentList = [],
  selectedSegments,
  selectedColumns = [],
  onFiltersChange,
}) => {
  const resolvedFilterOptionsUrl = filterOptionsUrl !== undefined
    ? filterOptionsUrl
    : (apiEndpoint && filterOptionsPath ? `${apiEndpoint}${filterOptionsPath}` : null);

  const filterContextKey = useMemo(
    () => `${resolvedFilterOptionsUrl || ""}|${JSON.stringify(metaChannelIds || [])}`,
    [resolvedFilterOptionsUrl, metaChannelIds]
  );

  const [filterPayload, setFilterPayload] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [appliedOperator, setAppliedOperator] = useState("AND");
  const [filterMetadata, setFilterMetadata] = useState(null);
  const [isFilterMetadataLoading, setIsFilterMetadataLoading] = useState(false);
  const [filterOpenTrigger, setFilterOpenTrigger] = useState(0);
  const [filterPayloadSourceKey, setFilterPayloadSourceKey] = useState(filterContextKey);
  const previousFilterContextKeyRef = useRef(filterContextKey);

  // A catalog change means the rules belong to a different reporting schema.
  // Clear them immediately and never forward a stale payload to the new API.
  // Some catalogs (e.g. Metasearch's Global Reporting) keep one URL for many
  // platform combinations, so selected meta channels are part of the key too.
  useEffect(() => {
    if (previousFilterContextKeyRef.current === filterContextKey) return;

    previousFilterContextKeyRef.current = filterContextKey;
    setFilterPayload([]);
    setAppliedFilters([]);
    setAppliedOperator("AND");
    setFilterMetadata(null);
    setFilterPayloadSourceKey(filterContextKey);
  }, [filterContextKey]);

  // ─── Fetch the filter-options catalog (metrics + segments) ──────────────────
  useEffect(() => {
    const hasScope = level === "HOTEL"
      ? hotelId.length > 0
      : level === "GROUP" && groupId.length > 0;

    if (!resolvedFilterOptionsUrl || !accountId || !level || !token || !hasScope) {
      setFilterMetadata(null);
      setIsFilterMetadataLoading(false);
      return undefined;
    }

    let cancelled = false;
    setFilterMetadata(null);
    setIsFilterMetadataLoading(true);

    const params = {
      account_id: accountId,
      level,
      ...(level === "HOTEL" ? { "hotel_id[]": hotelId } : { "group_id[]": groupId }),
      ...(campaign?.length ? { "campaign_id[]": campaign.map((c) => c.id) } : {}),
      ...(metaChannelIds?.length ? { "meta_channel[]": metaChannelIds } : {}),
      start_date: format(currentDateRange?.startDate || new Date(), "yyyy-MM-dd"),
      end_date: format(currentDateRange?.endDate || new Date(), "yyyy-MM-dd"),
    };

    axios
      .get(resolvedFilterOptionsUrl, {
        params: createSearchParams(params),
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) setFilterMetadata(res?.data || null);
      })
      .catch(() => {
        if (!cancelled) setFilterMetadata(null);
      })
      .finally(() => {
        if (!cancelled) setIsFilterMetadataLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resolvedFilterOptionsUrl,
    accountId,
    level,
    token,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(hotelId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(groupId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(campaign),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(metaChannelIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(currentDateRange),
  ]);

  // ─── Build the column options from metadata (segments + metrics) ────────────
  // Falls back to the reporting METRICS columns when metadata is unavailable
  // (either still loading, or filtering isn't supported for the current
  // product/platform/selection).
  const filterColumnOptions = useMemo(() => {
    if (!filterMetadata) {
      return selectedColumns
        .filter((c) => c?.type === "METRICS")
        .map((c) => ({ field: c?.accessorKey, label: c?.header, type: c?.type }));
    }

    const activeSegments = new Set(
      segmentList?.length ? segmentList : selectedSegments || []
    );

    const segmentOptions = (filterMetadata?.segments || [])
      .filter((s) => s?.filter_field)
      .map((s) => ({
        field: s.filter_field,
        label: s.label,
        type: "SEGMENT",
        fieldType: s.field_type,
        allowedOps: s.allowed_ops,
        filterValues: s.filter_values,
        segmentId: s.id,
        // Segment filters only apply server-side when the segment is active.
        isActive: activeSegments.has(s.id),
      }));

    const metricOptions = (filterMetadata?.metrics || []).map((m) => ({
      field: m.id,
      label: m.label,
      type: "METRICS",
      fieldType: m.is_percent ? "percent" : "numeric",
      allowedOps: m.allowed_ops,
      isActive: true,
    }));

    return [...segmentOptions, ...metricOptions];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterMetadata,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(segmentList),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(selectedSegments),
    selectedColumns,
  ]);

  // ─── Prune rules whose segment fields became inactive ──────────────────────
  useEffect(() => {
    if (!filterMetadata) return;

    const currentRules = filterPayload?.[0]?.rules || appliedFilters;
    if (!currentRules.length) return;

    const activeFields = new Set(
      filterColumnOptions.filter((o) => o.isActive !== false).map((o) => o.field)
    );

    // Keep only fields advertised by the current metadata and, for segments,
    // currently active in the report. This also removes fields invalidated by
    // a platform/catalog change, including rules pending server confirmation.
    const validRules = currentRules.filter((rule) => activeFields.has(rule.field));

    if (validRules.length !== currentRules.length) {
      setAppliedFilters((rules) => rules.filter((rule) => activeFields.has(rule.field)));
      onFiltersChange?.();
      if (validRules.length === 0) {
        setFilterPayload([]);
        setAppliedOperator("AND");
      } else {
        setFilterPayload([{
          combinator: filterPayload?.[0]?.combinator || appliedOperator.toLowerCase(),
          rules: validRules,
        }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterColumnOptions, filterMetadata]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const applyFilters = useCallback((payload) => {
    onFiltersChange?.();
    setFilterPayloadSourceKey(filterContextKey);
    setFilterPayload(payload);
  }, [filterContextKey, onFiltersChange]);

  const clearFilters = useCallback(() => {
    onFiltersChange?.();
    setFilterPayload([]);
    setAppliedFilters([]);
    setAppliedOperator("AND");
    setFilterPayloadSourceKey(filterContextKey);
  }, [filterContextKey, onFiltersChange]);

  const removeFilter = useCallback((index) => {
    setAppliedFilters((prev) => {
      const remaining = prev.filter((_, i) => i !== index);
      onFiltersChange?.();
      if (remaining.length === 0) {
        setFilterPayload([]);
        setAppliedOperator("AND");
      } else {
        setAppliedOperator((op) => {
          setFilterPayload([{ combinator: op.toLowerCase(), rules: remaining }]);
          return op;
        });
      }
      return remaining;
    });
  }, [onFiltersChange]);

  const openFilters = useCallback(() => setFilterOpenTrigger((prev) => prev + 1), []);


  const markFiltersApplied = useCallback(() => {
    if (filterPayload?.length > 0 && filterPayloadSourceKey === filterContextKey) {
      setAppliedFilters(filterPayload[0]?.rules || []);
      setAppliedOperator((filterPayload[0]?.combinator || "and").toUpperCase());
    } else {
      setAppliedFilters([]);
      setAppliedOperator("AND");
    }
  }, [filterContextKey, filterPayload, filterPayloadSourceKey]);

  // Params to spread into the reporting request. Also strips any rule whose
  // field is no longer active, guarding against a race between applying a
  // filter and a segment/platform change invalidating it before the report
  // request goes out.
  const tableFilterParams = useMemo(() => {
    if (!filterPayload?.length || filterPayloadSourceKey !== filterContextKey) {
      return {};
    }

    const activeFields = new Set(
      filterColumnOptions.filter((option) => option.isActive !== false).map((option) => option.field)
    );
    const requestPayload = filterMetadata
      ? filterPayload
          .map((group) => ({
            ...group,
            rules: (group.rules || []).filter((rule) => activeFields.has(rule.field)),
          }))
          .filter((group) => group.rules.length > 0)
      : filterPayload;

    return requestPayload.length > 0
      ? { table_filters: JSON.stringify(requestPayload) }
      : {};
  }, [filterColumnOptions, filterContextKey, filterMetadata, filterPayload, filterPayloadSourceKey]);

  return {
    filterPayload,
    filterMetadata,
    filterColumnOptions,
    appliedFilters,
    appliedOperator,
    filterOpenTrigger,
    tableFilterParams,
    isFilterMetadataLoading,
    isFilteringSupported: Boolean(resolvedFilterOptionsUrl),
    applyFilters,
    clearFilters,
    removeFilter,
    openFilters,
    markFiltersApplied,
  };
};

export default useReportingFilters;
